import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchMarketplaceProduct } from '../../api/products';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { useCart } from '../../context/CartContext';
import { stockBadgeLabel } from '../../i18n/helpers';

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [imageIndex, setImageIndex] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['marketplace-product', productId],
    queryFn: () => fetchMarketplaceProduct(productId),
  });

  const product = data?.product;

  if (isLoading) {
    return (
      <MarketplaceLayout>
        <p className="p-8 text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>
      </MarketplaceLayout>
    );
  }

  if (!product) {
    return (
      <MarketplaceLayout>
        <p className="p-8">{t('marketplace.productNotFound')}</p>
      </MarketplaceLayout>
    );
  }

  const images = product.images || [];
  const cp = product.campaignPricing;

  return (
    <MarketplaceLayout>
      <div className="mx-auto max-w-5xl px-6 py-8">
        <Link to="/marketplace" className="text-sm text-glow-400 hover:underline">{t('marketplace.back')}</Link>
        <div className="mt-6 grid gap-8 lg:grid-cols-2">
          <div>
            {images[imageIndex]?.url ? (
              <img src={images[imageIndex].url} alt="" className="aspect-square w-full rounded-xl object-cover" />
            ) : (
              <div className="flex aspect-square items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800">{t('common.noImage')}</div>
            )}
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {images.map((img, i) => (
                <button key={i} type="button" onClick={() => setImageIndex(i)} className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${i === imageIndex ? 'border-glow-500' : 'border-zinc-300 dark:border-zinc-700'}`}>
                  <img src={img.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{product.name}</h1>
            {cp ? (
              <div className="mt-2">
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  {cp.discountMode === 'PERCENTAGE'
                    ? t('marketplace.percentOff', { value: cp.discountValue })
                    : t('marketplace.flatOff', { value: Number(cp.discountValue || 0).toLocaleString() })}
                </p>
                <p className="text-2xl font-bold text-glow-300">
                  {t('common.currency', { amount: Number(cp.finalPrice || 0).toLocaleString() })}
                  <span className="ml-2 text-base font-normal text-zinc-500 line-through">
                    {t('common.currency', { amount: Number(cp.basePrice || 0).toLocaleString() })}
                  </span>
                </p>
              </div>
            ) : (
              <p className="mt-2 text-2xl font-bold text-glow-300">{t('common.currency', { amount: product.price?.toLocaleString() })}</p>
            )}
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">
              {t('marketplace.stockInCount', { count: product.stock })} · {stockBadgeLabel(product.stockBadge)}
            </p>
            <p className="mt-4 text-zinc-600 dark:text-zinc-400">{product.description}</p>
            <div className="mt-6 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <p className="font-medium">{product.salonName}</p>
              <p className="text-sm text-zinc-600 dark:text-zinc-500">{product.salonAddress || ''}</p>
              <p className="text-xs text-amber-400">★ {product.salonRating}</p>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <input type="number" min={1} max={product.stock} value={qty} onChange={(e) => setQty(Number(e.target.value))} className="w-20 rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-2 text-sm" />
              <button type="button" onClick={() => addItem(product, qty)} className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-4 py-2 text-sm">{t('common.addToCart')}</button>
              <button
                type="button"
                onClick={() => {
                  addItem(product, qty);
                  navigate('/client/checkout');
                }}
                className="rounded-lg bg-glow-600 px-4 py-2 text-sm text-white"
              >
                {t('common.orderNow')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MarketplaceLayout>
  );
};

export default ProductDetailPage;
